/**
 * AdminZugangsdatenSection — Zugangsdaten-Verwaltung (nur Admin)
 * 
 * Zeigt alle API-Keys, Tokens und Zugangsdaten gruppiert nach Kategorie.
 * Werte werden maskiert angezeigt und können per Klick aufgedeckt werden.
 * CRUD-Operationen über useZugangsdaten Hook.
 */

import { useState } from 'react';
import {
  Key, Eye, EyeOff, Plus, Edit2, Trash2, X, Save,
  Server, Database, GitBranch, Cpu, Globe, Shield,
  Copy, Check, ChevronDown, ChevronRight, Lock,
} from 'lucide-react';
import { useZugangsdaten, type Zugangsdaten } from '@/hooks/useAdminSettings';
import { toast } from 'sonner';

const KATEGORIE_ICONS: Record<string, { icon: any; color: string; bg: string }> = {
  'Hosting': { icon: Server, color: '#2563EB', bg: 'rgba(37,99,235,0.08)' },
  'Datenbank': { icon: Database, color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  'Versionskontrolle': { icon: GitBranch, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  'KI': { icon: Cpu, color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
  'API': { icon: Globe, color: '#0D9488', bg: 'rgba(13,148,136,0.08)' },
};

function MaskValue({ value, isSecret }: { value: string; isSecret: boolean }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('In Zwischenablage kopiert');
  };

  if (!isSecret) {
    return (
      <div className="flex items-center gap-2">
        <code className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded break-all">{value}</code>
        <button onClick={handleCopy} className="p-1 rounded hover:bg-gray-100 flex-shrink-0">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <code className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded break-all">
        {visible ? value : value.substring(0, 8) + '••••••••••••'}
      </code>
      <button onClick={() => setVisible(!visible)} className="p-1 rounded hover:bg-gray-100 flex-shrink-0" title={visible ? 'Verbergen' : 'Anzeigen'}>
        {visible ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
      </button>
      <button onClick={handleCopy} className="p-1 rounded hover:bg-gray-100 flex-shrink-0" title="Kopieren">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
      </button>
    </div>
  );
}

interface EditFormData {
  kategorie: string;
  dienst: string;
  schluessel: string;
  wert: string;
  beschreibung: string;
  ist_geheim: boolean;
}

const EMPTY_FORM: EditFormData = {
  kategorie: 'API',
  dienst: '',
  schluessel: '',
  wert: '',
  beschreibung: '',
  ist_geheim: true,
};

export default function AdminZugangsdatenSection() {
  const { data, loading, create, update, remove } = useZugangsdaten();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState<EditFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Gruppiere nach Kategorie
  const grouped = data.reduce<Record<string, Zugangsdaten[]>>((acc, item) => {
    if (!acc[item.kategorie]) acc[item.kategorie] = [];
    acc[item.kategorie].push(item);
    return acc;
  }, {});

  const toggleCategory = (cat: string) => {
    const next = new Set(expandedCategories);
    if (next.has(cat)) next.delete(cat); else next.add(cat);
    setExpandedCategories(next);
  };

  const handleSave = async () => {
    if (!formData.dienst || !formData.schluessel || !formData.wert) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    setSaving(true);
    if (editingId) {
      await update(editingId, formData);
    } else {
      await create(formData);
    }
    setSaving(false);
    setEditingId(null);
    setShowCreate(false);
    setFormData(EMPTY_FORM);
  };

  const handleEdit = (item: Zugangsdaten) => {
    setFormData({
      kategorie: item.kategorie,
      dienst: item.dienst,
      schluessel: item.schluessel,
      wert: item.wert,
      beschreibung: item.beschreibung || '',
      ist_geheim: item.ist_geheim,
    });
    setEditingId(item.id);
    setShowCreate(true);
  };

  const handleDelete = async (item: Zugangsdaten) => {
    if (!window.confirm(`"${item.schluessel}" wirklich löschen?`)) return;
    await remove(item.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-[#9B1C1C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#9B1C1C]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Zugangsdaten & API-Keys</h2>
              <p className="text-xs text-gray-400">Vercel, Supabase, GitHub, OpenAI — sicher verschlüsselt, nur für Admin</p>
            </div>
          </div>
          <button
            onClick={() => { setShowCreate(true); setEditingId(null); setFormData(EMPTY_FORM); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Hinzufügen
          </button>
        </div>

        {/* Sicherheitshinweis */}
        <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-[11px] text-amber-700">
            Diese Daten sind durch Row Level Security geschützt und nur für den Admin-Account sichtbar.
            Werte werden maskiert angezeigt.
          </p>
        </div>
      </div>

      {/* Kategorien */}
      {Object.entries(grouped).map(([kategorie, items]) => {
        const catInfo = KATEGORIE_ICONS[kategorie] || { icon: Key, color: '#6B7280', bg: 'rgba(107,114,128,0.08)' };
        const CatIcon = catInfo.icon;
        const isExpanded = expandedCategories.has(kategorie) || expandedCategories.size === 0;

        return (
          <div key={kategorie} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleCategory(kategorie)}
              className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catInfo.bg }}>
                <CatIcon className="w-4 h-4" style={{ color: catInfo.color }} />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-bold text-gray-800">{kategorie}</p>
                <p className="text-[10px] text-gray-400">{items.length} Einträge</p>
              </div>
              {isExpanded
                ? <ChevronDown className="w-4 h-4 text-gray-400" />
                : <ChevronRight className="w-4 h-4 text-gray-400" />
              }
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100">
                {items.map((item, i) => (
                  <div
                    key={item.id}
                    className={`px-5 py-3 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} border-b border-gray-50 last:border-0`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-bold text-gray-800">{item.dienst}</p>
                          <code className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{item.schluessel}</code>
                          {item.ist_geheim && (
                            <span className="text-[9px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">geheim</span>
                          )}
                        </div>
                        <MaskValue value={item.wert} isSecret={item.ist_geheim} />
                        {item.beschreibung && (
                          <p className="text-[11px] text-gray-400 mt-1">{item.beschreibung}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                          title="Bearbeiten"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 rounded hover:bg-red-50 transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {data.length === 0 && !loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center shadow-sm">
          <Key className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-400">Keine Zugangsdaten hinterlegt</p>
          <p className="text-xs text-gray-300 mt-1">Füge API-Keys, Tokens und Zugangsdaten hinzu</p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowCreate(false); setEditingId(null); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editingId ? 'Zugangsdaten bearbeiten' : 'Neue Zugangsdaten'}
                </h2>
                <p className="text-xs text-gray-400">Alle Felder mit * sind Pflichtfelder</p>
              </div>
              <button onClick={() => { setShowCreate(false); setEditingId(null); }} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Kategorie *</label>
                  <select
                    value={formData.kategorie}
                    onChange={e => setFormData(p => ({ ...p, kategorie: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                  >
                    <option value="Hosting">Hosting</option>
                    <option value="Datenbank">Datenbank</option>
                    <option value="Versionskontrolle">Versionskontrolle</option>
                    <option value="KI">KI</option>
                    <option value="API">API</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Dienst *</label>
                  <input
                    value={formData.dienst}
                    onChange={e => setFormData(p => ({ ...p, dienst: e.target.value }))}
                    placeholder="z.B. Vercel, Supabase"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Schlüssel-Name *</label>
                <input
                  value={formData.schluessel}
                  onChange={e => setFormData(p => ({ ...p, schluessel: e.target.value }))}
                  placeholder="z.B. VERCEL_TOKEN, SUPABASE_URL"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Wert *</label>
                <textarea
                  value={formData.wert}
                  onChange={e => setFormData(p => ({ ...p, wert: e.target.value }))}
                  placeholder="API-Key, Token oder URL eingeben"
                  rows={2}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20 resize-none"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500">Beschreibung</label>
                <input
                  value={formData.beschreibung}
                  onChange={e => setFormData(p => ({ ...p, beschreibung: e.target.value }))}
                  placeholder="Wofür wird dieser Key verwendet?"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setFormData(p => ({ ...p, ist_geheim: !p.ist_geheim }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.ist_geheim ? 'bg-[#C41E3A]' : 'bg-gray-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${formData.ist_geheim ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-xs text-gray-600">Geheimer Wert (wird maskiert angezeigt)</span>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => { setShowCreate(false); setEditingId(null); }}
                  className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#9B1C1C] hover:bg-[#7F1D1D] disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingId ? 'Aktualisieren' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
